import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { api } from '@jupi/ui'
import { useEnrolments, useProgress } from '../api/school'

// The flight school part of the booking form, shown for a training booking:
// which enrolment and lesson it is for, and with which instructor. Editing
// keeps the enrolment and lets the lesson and instructor change.
// Instructors already booked at
// the chosen time are shown but cannot be picked; the API refuses them
// too (instructor_busy).

export interface LessonChoice {
  enrolmentId: number | null
  lessonId: number | null
  instructorUserId: string | null
}

interface Availability {
  instructors: { userId: string; displayName: string; typeRatings: string[]; busy: { startsAt: string; endsAt: string }[] }[]
}

export function LessonBookingSection({
  studentUserId,
  value,
  onChange,
  window,
  aircraftType,
  editing = false,
}: {
  studentUserId: string | undefined
  value: LessonChoice
  onChange: (value: LessonChoice) => void
  window: { startsAt: Date; endsAt: Date } | null
  aircraftType: string | null
  editing?: boolean
}) {
  const { t } = useTranslation()
  const { data: enrolments = [] } = useEnrolments({ studentUserId })
  const active = enrolments.filter((e) => e.status === 'active' && e.studentUserId === studentUserId)
  const enrolment = active.find((e) => e.enrolmentId === value.enrolmentId)
  const { data: progress } = useProgress(enrolment ? enrolment.enrolmentId : Number.NaN)
  const lessons = useMemo(() => progress?.modules.flatMap((m) => m.lessons) ?? [], [progress])

  // One enrolment is the common case: pick it; then default the lesson to
  // the next one not yet done.
  useEffect(() => {
    if (value.enrolmentId === null && active.length === 1) {
      onChange({ ...value, enrolmentId: active[0]!.enrolmentId })
    }
  }, [value, active, onChange])
  useEffect(() => {
    // A booking being edited keeps no lesson if it had none ("extra flight").
    if (!editing && enrolment && value.lessonId === null && lessons.length > 0) {
      const next = lessons.find((l) => l.status !== 'completed')
      if (next) onChange({ ...value, lessonId: next.lessonId })
    }
  }, [value, enrolment, lessons, editing, onChange])

  // The window is read in the browser's zone, which for this club is the
  // club's own; it only decides what to grey out -- the API checks the
  // instructor's diary for real. A day either side, so a busy slot next to
  // the window shows too.
  const range = window
    ? { from: new Date(window.startsAt.getTime() - 86_400_000), to: new Date(window.endsAt.getTime() + 86_400_000) }
    : null
  const { data: availability } = useQuery({
    queryKey: ['school', 'availability', enrolment?.courseCode, range?.from.toISOString(), range?.to.toISOString()],
    queryFn: () =>
      api.get<Availability>(
        `/api/school/instructors/availability?courseCode=${enrolment!.courseCode}&from=${range!.from.toISOString()}&to=${range!.to.toISOString()}`,
      ),
    enabled: Boolean(enrolment && range),
  })
  const busyAt = (busy: { startsAt: string; endsAt: string }[]) =>
    Boolean(window) && busy.some((b) => new Date(b.startsAt) < window!.endsAt && new Date(b.endsAt) > window!.startsAt)

  if (active.length === 0) return <Alert severity="info">{t('booking.type.noEnrolment')}</Alert>
  return (
    <Stack spacing={2}>
      <>
          {active.length > 1 && !editing ? (
            <TextField
              select
              label={t('school.course')}
              value={value.enrolmentId ?? ''}
              onChange={(e) => onChange({ ...value, enrolmentId: Number(e.target.value), lessonId: null, instructorUserId: null })}
            >
              {active.map((e) => (
                <MenuItem key={e.enrolmentId} value={e.enrolmentId}>
                  {e.courseName}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {enrolment?.courseName}
            </Typography>
          )}
          <TextField
            select
            label={t('school.lesson')}
            value={value.lessonId ?? ''}
            onChange={(e) => onChange({ ...value, lessonId: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <MenuItem value="">{t('school.extraFlight')}</MenuItem>
            {lessons.map((l) => (
              <MenuItem key={l.lessonId} value={l.lessonId}>
                {l.lessonNumber} {l.title}
                {l.status !== 'not_started' ? ` · ${t(`school.lesson_${l.status}`)}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('school.instructor')}
            value={value.instructorUserId ?? ''}
            onChange={(e) => onChange({ ...value, instructorUserId: e.target.value || null })}
            helperText={window ? ' ' : t('school.pickTimeFirst')}
          >
            <MenuItem value="">{t('school.noInstructor')}</MenuItem>
            {(availability?.instructors ?? []).map((i) => {
              const busy = busyAt(i.busy)
              return (
                <MenuItem key={i.userId} value={i.userId} disabled={busy}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
                    <span>{i.displayName}</span>
                    {busy ? <Chip size="small" color="warning" label={t('school.busy')} /> : null}
                    {aircraftType && i.typeRatings.includes(aircraftType) ? (
                      <Chip size="small" variant="outlined" label={aircraftType} />
                    ) : null}
                  </Stack>
                </MenuItem>
              )
            })}
          </TextField>
      </>
    </Stack>
  )
}
